import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Select } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import financialService from '@services/financialService';
import { checkingAccountService } from '@services/checkingAccountService';
import companyService from '@services/companyService';
import { CreditCard, FinancialAccount } from '@/types/financial';
import { Bank } from '@/types/checkingAccount';
import { Company, formatCnpj } from '@/types/company';
import { buildCategoryOptions, CategoryOptionGroup } from '@pages/financial/financialUtils';

/**
 * Cadastro de cartões de crédito da empresa ([Financial].[CreditCard]).
 * As faturas e os lançamentos ficam na tela Financeiro › Cartões de Crédito.
 */
export const CreditCardListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<CreditCard[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [accounts, setAccounts] = useState<FinancialAccount[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [categoryGroups, setCategoryGroups] = useState<CategoryOptionGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  useEffect(() => {
    companyService
      .getCompanies()
      .then((cs) => {
        setCompanies(cs);
        if (cs.length > 0) setCompanyId(cs[0].company_id);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Erro ao carregar empresas'));
    checkingAccountService
      .listBanks()
      .then(setBanks)
      .catch(() => setBanks([]));
  }, []);

  const loadItems = async (company: number) => {
    try {
      setLoading(true);
      setError(null);
      setItems(await financialService.getCreditCards(company));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar cartões');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId == null) return;
    loadItems(companyId);
    // Rótulos de conta e categoria: só para exibição, falha não bloqueia a lista.
    checkingAccountService
      .getAll(companyId)
      .then(setAccounts)
      .catch(() => setAccounts([]));
    financialService
      .getAccountingPlans(companyId)
      .then((tree) => setCategoryGroups(buildCategoryOptions(tree)))
      .catch(() => setCategoryGroups([]));
  }, [companyId]);

  const accountLabel = useMemo(() => {
    const byId = new Map(accounts.map((a) => [a.idAccountsMain, a.accountsDescription]));
    return (id: number | null) => (id == null ? '—' : byId.get(id) ?? `Conta ${id}`);
  }, [accounts]);

  const categoryLabel = useMemo(() => {
    const byId = new Map<number, string>();
    categoryGroups.forEach((g) => g.options.forEach((o) => byId.set(o.value, `${g.label} › ${o.label}`)));
    return (id: number | null) => (id == null ? '—' : byId.get(id) ?? `Categoria ${id}`);
  }, [categoryGroups]);

  const bankLabel = (bankNumber: string | null) => {
    if (!bankNumber) return '—';
    const bank = banks.find((b) => b.bankNumber === bankNumber);
    return bank?.bankName ? `${bankNumber} — ${bank.bankName}` : bankNumber;
  };

  const handleDelete = async (id: number) => {
    try {
      await financialService.deleteCreditCard(id);
      setDeleteConfirm(null);
      if (companyId != null) loadItems(companyId);
    } catch (err) {
      setDeleteConfirm(null);
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Cartões de Crédito</h1>
          <p className="text-slate-600">Cadastro dos cartões corporativos: fechamento, vencimento e onde a fatura é paga</p>
        </div>
        <PermissionGuard permission="financial.credit_card.write">
          <Button onClick={() => navigate('/settings/financial/credit-cards/new')} icon={<Plus size={20} />}>
            Novo Cartão
          </Button>
        </PermissionGuard>
      </div>

      {/* Filtro por empresa */}
      <Card>
        <CardContent>
          <div className="max-w-md">
            <Select
              label="Empresa"
              value={companyId ?? ''}
              onChange={(e) => setCompanyId(e.target.value === '' ? null : Number(e.target.value))}
            >
              {companies.map((c) => (
                <option key={c.company_id} value={c.company_id}>
                  {formatCnpj(c.document)} — {c.name}
                </option>
              ))}
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">Descrição</th>
                <th className="text-left">Banco</th>
                <th className="text-center">Fechamento</th>
                <th className="text-center">Vencimento</th>
                <th className="text-left">Conta de pagamento</th>
                <th className="text-left">Categoria da fatura</th>
                <th className="text-right">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(7)].map((_, j) => (
                      <TableCell key={j}><div className="h-4 bg-slate-200 rounded w-24 animate-pulse" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                    Nenhum cartão cadastrado para esta empresa
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.idCreditCards}>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell className="text-sm">{bankLabel(item.bankNumber)}</TableCell>
                    <TableCell className="text-center text-sm">{item.billingCycleDay ? `dia ${item.billingCycleDay}` : '—'}</TableCell>
                    <TableCell className="text-center text-sm">{item.billingDueDay ? `dia ${item.billingDueDay}` : '—'}</TableCell>
                    <TableCell className="text-sm">{accountLabel(item.idAccountsMain)}</TableCell>
                    <TableCell className="text-sm">{categoryLabel(item.accountingPlanErpId)}</TableCell>
                    <TableCell className="text-right">
                      <PermissionGuard permission="financial.credit_card.write">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/settings/financial/credit-cards/${item.idCreditCards}/edit`)}
                            icon={<Edit size={14} />}
                          >
                            Editar
                          </Button>
                          {deleteConfirm === item.idCreditCards ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.idCreditCards)}>
                                Confirmar
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.idCreditCards)}
                              icon={<Trash2 size={14} />}
                            >
                              Excluir
                            </Button>
                          )}
                        </div>
                      </PermissionGuard>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500">
        Cartão com faturas, lançamentos, recorrências ou parcelas de compra não pode ser excluído.
      </p>
    </div>
  );
};
